import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, NgForm, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatStepper } from '@angular/material/stepper';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { AuthService } from 'src/app/services/auth.service';
import { DrupalRESTService } from 'src/app/services/drupal-rest.service';
import { EntityRESTService } from 'src/app/services/entity-rest.service';
import { FieldsService } from 'src/app/services/fields.service';
import { TaxonomyService } from 'src/app/services/taxonomy.service';
import { UtilityService } from 'src/app/services/utility.service';
import { LoggerService } from '../../../services/logger.service';

@Component({
  selector: 'app-frm-manage-gift-certificate',
  templateUrl: './frm-manage-gift-certificate.component.html',
  styleUrls: ['./frm-manage-gift-certificate.component.css']
})
export class FrmManageGiftCertificateComponent implements OnInit {
  actionType: string;
  giftCertificateForm: FormGroup;
  purchaserForm: FormGroup;
  recipientForm: FormGroup;
  paymentForm: FormGroup;
  autoCompletePurchaserOptions: any;
  paymentSubmitted = false;
  giftCertificateID: string;

  @ViewChild('stepper') stepper: MatStepper;

  /**
   * Index of the visible step, so the single dialog actions row can show the
   * action that belongs to it.
   */
  currentStep = 0;
  autoCompletePackageOptions: Object;
  selectedPackageSPSCode: any;
  /**
   * The selected package's pricing: what it costs before its discount, the
   * discount taken off, and the discount as the package stores it. Sent with
   * the sale so the enrollment is priced the same way, and shown under the
   * amount so staff can see the discount was applied.
   */
  selectedPackagePricing: { subtotal: number; discount: number; discountValue: number; discountType: string } | null = null;

  constructor(
    private formBuilder: FormBuilder,
    private entityRestService: EntityRESTService,
    public DialogRef: MatDialogRef<FrmManageGiftCertificateComponent>,
    private utilityService: UtilityService,
    private drupalRESTService: DrupalRESTService,
    public taxonomyService: TaxonomyService,
    private snackBar: MatSnackBar,
    public _fieldsService: FieldsService,
    private authService: AuthService,
    @Inject(MAT_DIALOG_DATA) public injectedDialogData: any
  ) { }

  ngOnInit() {
    // The dialog is opened with { id, action }; without reading it actionType
    // stayed undefined and the edit title never rendered.
    this.actionType = this.injectedDialogData?.action ?? 'create';
    this.giftCertificateID = this.injectedDialogData?.id ?? undefined;

    const currentDate = new Date();
    const expirationDate = new Date(currentDate.setMonth(currentDate.getMonth() + 6));

    this.giftCertificateForm = this.formBuilder.group({
      giftCertificateCode: ['', Validators.required],
      giftCertificateAmount: ['', [Validators.required, Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
      field_executive: ['', Validators.required],
      field_enrollment_package_name: ['', Validators.required],
      field_enrollment_lesson_count: ['', Validators.required],
      field_enrollment_lesson_price: ['', Validators.required],
      expirationDate: [expirationDate, Validators.required],
      giftCertificateNotes: [''],
      // The purchaser's note to the recipient; printed on the certificate.
      giftMessage: [''],
      checkoutType: ['guest', Validators.required],
      purchaserName: [''],
      purchaserEmail: ['', [Validators.required, Validators.email]],
      purchaserPhone: ['', Validators.required],
      giftCertificatePurchaser: [''],
      field_recipient_name: ['', Validators.required],
      recipientEmail: ['', [Validators.required, Validators.email]],
      recipientPhone: ['', Validators.required],
      // Optional: hold the certificate email until this calendar date.
      emailSendDate: [''],
    });

    this.paymentForm = this.formBuilder.group({
      field_payment_type: ['', Validators.required],
      cc_number: [''],
      cc_expiration: [''],
      cc_cvv: [''],
      // Read by onSubmit() when building the payment; without it the
      // reference number was always undefined.
      referenceNumber: [''],
    });

    this.loadUniqueGCCode();
  }

  private loadUniqueGCCode() {
    this.drupalRESTService.httpGET('/api_rest/v1/uniqueGCCode').subscribe(
      (response: any) => {
        if (response.status === 'success') {
          this.giftCertificateForm.patchValue({
            giftCertificateCode: response.code
          });
        }
      },
      (error) => {
        LoggerService.error('Error loading unique gift certificate code:', error);
      }
    );
  }

  onSubmit() {
    if (this.giftCertificateForm.valid && this.paymentForm.valid) {
      const expirationDateString = this.giftCertificateForm.value.expirationDate;
      const expirationDate = new Date(expirationDateString);
      // The picker returns local midnight. toISOString() would convert that to
      // UTC and move the date back a day for any studio west of Greenwich, so
      // the calendar date is formatted from local parts instead.
      const formattedExpirationDate = this.formatDate(expirationDate);
      // Expire at the end of the chosen day rather than the start of it.
      const expirationDateISO = `${formattedExpirationDate}T23:59:59`;

      const linkedStudentId = this.giftCertificateForm.value.checkoutType === 'existing'
        ? this.regexStudentName(this.giftCertificateForm.value.giftCertificatePurchaser)
        : undefined;

      // One request creates the enrollment, payment and certificate inside a
      // single server transaction. The previous four-request sequence
      // orphaned the payment mid-flight and could strand it if any later
      // request failed, with nothing shown to the user.
      const emailSendDate = this.giftCertificateForm.value.emailSendDate;
      const body = {
        code: this.giftCertificateForm.value.giftCertificateCode,
        amount: this.giftCertificateForm.value.giftCertificateAmount,
        package_id: this.regexParamID(this.giftCertificateForm.value.field_enrollment_package_name),
        category_id: this.mapSPSCodeToCategory(this.selectedPackageSPSCode),
        lesson_count: this.giftCertificateForm.value.field_enrollment_lesson_count,
        lesson_price: this.giftCertificateForm.value.field_enrollment_lesson_price,
        discount: this.selectedPackagePricing?.discountValue ?? 0,
        discount_type: this.selectedPackagePricing?.discountType ?? 'flat',
        executive_id: this.giftCertificateForm.value.field_executive,
        expiration_date: formattedExpirationDate,
        expiration_date_time: expirationDateISO,
        email_send_date: emailSendDate ? this.formatDate(new Date(emailSendDate)) : null,
        notes: this.giftCertificateForm.value.giftCertificateNotes,
        gift_message: this.giftCertificateForm.value.giftMessage,
        checkout_type: this.giftCertificateForm.value.checkoutType,
        purchaser_student_id: linkedStudentId,
        purchaser_name: this.giftCertificateForm.value.purchaserName,
        purchaser_email: this.giftCertificateForm.value.purchaserEmail,
        purchaser_phone: this.giftCertificateForm.value.purchaserPhone,
        recipient_name: this.giftCertificateForm.value.field_recipient_name,
        recipient_email: this.giftCertificateForm.value.recipientEmail,
        recipient_phone: this.giftCertificateForm.value.recipientPhone,
        payment_type: this.paymentForm.value.field_payment_type,
        reference_number: this.paymentForm.value.referenceNumber,
        field_studio_reference: this.authService.getCurrentStudioId(),
      };

      this.drupalRESTService.httpPOST('/api_rest/v1/issueGiftCertificate', body)
        .subscribe(
          (response: any) => {
            const result = response?.body ?? response;
            if (!result || result.status !== 'success') {
              this.snackBar.open(result?.message || 'Could not issue the gift certificate.', 'Close', { duration: 6000 });
              return;
            }
            // Only lock the earlier steps once the sale actually went through,
            // matching the previous flow; locking them before the request
            // left the review step blank.
            this.paymentSubmitted = true;
            this.giftCertificateID = result.id;
            this.goToNextStep(this.stepper);
          },
          (error) => {
            LoggerService.error('Error issuing gift certificate:', error);
            this.snackBar.open('Could not issue the gift certificate.', 'Close', { duration: 6000 });
          }
        );
    }
  }

  onCheckoutTypeChange(event: any) {
    const checkoutType = event.value;
    const purchaserNameControl = this.giftCertificateForm.get('purchaserName');
    const purchaserEmailControl = this.giftCertificateForm.get('purchaserEmail');
    const purchaserPhoneControl = this.giftCertificateForm.get('purchaserPhone');
    const giftCertificatePurchaserControl = this.giftCertificateForm.get('giftCertificatePurchaser');

    // Purchaser contact details are always required. A certificate can be
    // bought without a student account. Linking an account is optional and,
    // when chosen, fills these in from the account.
    purchaserNameControl.setValidators([Validators.required]);
    purchaserEmailControl.setValidators([Validators.required, Validators.email]);
    purchaserPhoneControl.setValidators([Validators.required]);
    if (checkoutType === 'existing') {
      giftCertificatePurchaserControl.setValidators([Validators.required]);
    } else {
      giftCertificatePurchaserControl.clearValidators();
      giftCertificatePurchaserControl.setValue('');
    }

    purchaserNameControl.updateValueAndValidity();
    purchaserEmailControl.updateValueAndValidity();
    purchaserPhoneControl.updateValueAndValidity();
    giftCertificatePurchaserControl.updateValueAndValidity();
  }

  formatDate(date: Date): string {
    // Built from local parts on purpose: toISOString() shifts to UTC and can
    // roll a user-picked calendar date back by one day.
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  isGiftCertificateFormValid(): boolean {
    const form = this.giftCertificateForm;
    // The amount is derived from the package, so a package is required.
    const requiredControls = [
      'giftCertificateCode', 'field_enrollment_package_name', 'field_executive',
      'giftCertificateAmount', 'expirationDate', 'checkoutType',
      'purchaserName', 'purchaserEmail', 'purchaserPhone',
    ];

    if (form.get('checkoutType').value === 'existing') {
      requiredControls.push('giftCertificatePurchaser');
    }

    requiredControls.push('field_recipient_name', 'recipientEmail', 'recipientPhone');

    return requiredControls.every(controlName => {
      const control = form.get(controlName);
      return control && control.valid;
    });
  }

  onAutocompletePurchaser(event: { target: { value: string } }) {
    let term: string = event.target.value;
    let eckType: string = "student_accounts"; // student_accounts
    let bundle: string = "student_account"; // student_account
    let field: string = "title";
    this.utilityService.getAMTAbstractAutocomplete(term, eckType, bundle, field)
      .subscribe(data => {
        this.autoCompletePurchaserOptions = data;
      })
  }

  /**
 * This function is to validate form controls recursively.
 *
 * @param formToInvestigate
 * @returns
 */
  findInvalidControlsRecursive(formToInvestigate: FormGroup | FormArray): string[] {
    var invalidControls: string[] = [];
    let recursiveFunc = (form: FormGroup | FormArray) => {
      Object.keys(form.controls).forEach(field => {
        const control = form.get(field);
        if (control.invalid) invalidControls.push(field);
        if (control instanceof FormGroup) {
          recursiveFunc(control);
        } else if (control instanceof FormArray) {
          recursiveFunc(control);
        }
      });
    }
    recursiveFunc(formToInvestigate);
    return invalidControls;
  }

  onStepChange(event: StepperSelectionEvent) {
    this.currentStep = event.selectedIndex;
  }

  goToNextStep(stepper: MatStepper) {
    if (this.isGiftCertificateFormValid()) {
      stepper.next();
    }
  }

  regexStudentName(field_student_name: string) {
    if (!field_student_name) { return; }
    if (!isNaN(+field_student_name)) { return; }

    // Adjusted regex to match the content of the last set of parentheses
    let regExp = /\(([^)]+)\)(?!.*\()/;
    let matches = regExp.exec(field_student_name);

    if (!matches) { return; }

    return matches[1];
  }

  getPaymentTypeName(paymentTypeId: string): string {
    // payment_type_all: an existing purchase may use a method the studio has
    // since hidden from the picker.
    const paymentType = this.taxonomyService.payment_type_all.find(type => type.id === paymentTypeId);
    return paymentType ? paymentType.name : '';
  }

  lookupStudentAccount(event: any, populateFields: boolean = false) {
    LoggerService.log('Event:', event.source.value);

    if (event && event.source && event.source.value) {
      const selectedOption = event.source.value;
      const matches = /\(([^)]+)\)(?!.*\()/.exec(selectedOption);

      if (matches) {
        const studentAccountId = matches[1];

        this.entityRestService.getEntity('student_accounts', 'student_account', studentAccountId)
          .subscribe(
            (response: any) => {
              if (populateFields) {
                LoggerService.log('Student account data:', response);

                if (Array.isArray(response.field_contacts)) {
                  const primaryContact = response.field_contacts.find((contact: any) => contact.field_first_name && contact.field_last_name);

                  if (primaryContact) {
                    this.giftCertificateForm.patchValue({
                      purchaserName: primaryContact.field_first_name + ' ' + primaryContact.field_last_name,
                      purchaserEmail: primaryContact.field_email,
                      purchaserPhone: primaryContact.field_cell_phone ? primaryContact.field_cell_phone : primaryContact.field_home_phone,
                    });
                  } else {
                    this.giftCertificateForm.patchValue({
                      purchaserName: '',
                      purchaserEmail: '',
                      purchaserPhone: '',
                    });
                  }
                } else {
                  this.giftCertificateForm.patchValue({
                    purchaserName: response.field_contacts?.field_first_name + ' ' + response.field_contacts?.field_last_name,
                    purchaserEmail: response.field_contacts?.field_email,
                    purchaserPhone: response.field_contacts?.field_cell_phone ? response.field_contacts?.field_cell_phone : response.field_contacts?.field_home_phone,
                  });
                }

                LoggerService.log('Purchase name:', this.giftCertificateForm.get('purchaserName').value);
                LoggerService.log('Purchase email:', this.giftCertificateForm.get('purchaserEmail').value);
                LoggerService.log('Purchase phone:', this.giftCertificateForm.get('purchaserPhone').value);
              }
            },
            (error) => {
              LoggerService.error('Error retrieving student account data:', error);
            }
          );
      }
    }
  }

  sendGiftCertificateEmail(giftCertificateID: string) {
    this.drupalRESTService.httpGET('/api_rest/v1/sendGiftCertificateEmail?giftCertificateID=' + giftCertificateID)
      .subscribe(
        data => {
          LoggerService.log('API response:', data);
          if (data['status'] === 'error') {
            const errorMessage = data['message'] || 'An error occurred while sending the email.';
            // Snackbar message that is centered.
            this.snackBar.open(errorMessage, 'Close', {
              duration: 3000,
              horizontalPosition: 'center',
            });
          } else {
            // Snackbar message that is centered.
            this.snackBar.open('Gift certificate email sent successfully.', 'Close', {
              duration: 3000,
              horizontalPosition: 'center',
            });
          }
        },
        error => {
          LoggerService.error('Error sending email:', error);
          // Snackbar message that is centered.
          this.snackBar.open('An error occurred while sending the email.', 'Close', {
            duration: 3000,
            horizontalPosition: 'center',
          });
        }
      );
  }

  /** Texts the certificate to the recipient's phone from the confirmation step. */
  sendGiftCertificateSms(giftCertificateID: string) {
    this.drupalRESTService.httpGET('/api_rest/v1/sendGiftCertificateSMS?giftCertificateID=' + giftCertificateID)
      .subscribe(
        (data: any) => {
          const message = data?.error ? data.error : 'Gift certificate text message sent.';
          this.snackBar.open(message, 'Close', { duration: data?.error ? 6000 : 3000, horizontalPosition: 'center' });
        },
        (error) => {
          LoggerService.error('Error sending gift certificate text:', error);
          this.snackBar.open('Could not send the gift certificate text message.', 'Close', { duration: 6000, horizontalPosition: 'center' });
        }
      );
  }

  // Handle the autocomplete for package field.
  onAutocompletePackages($event: { target: { value: string; }; }) {
    let term: string = $event.target.value;
    let eckType: string = 'packages';
    let bundle: string = 'package';
    let field: string = 'title';

    this.utilityService.getAMTAbstractAutocomplete(term, eckType, bundle, field)
      .subscribe(data => {
        // Filter packages to only show ones with field_is_gift_certificate = true
        this.autoCompletePackageOptions = Object.values(data).filter((pkg: any) =>
          pkg.field_is_gift_certificate == true
        );
      });
  }

  onPackageLookup($event: { option: { value: any; }; }) {

    this.entityRestService.getEntity('packages', 'package', this.regexParamID($event.option.value))
      .subscribe(response => {
        // Store the SPS code.
        this.selectedPackageSPSCode = response['field_sps_code'];

        // Price the package the way the package form does: misc and sundry
        // items are one flat price, everything else is lessons times price,
        // and the package's discount comes off before it becomes the
        // certificate amount. Lesson price times count alone sold a
        // discounted package at full price.
        const price = parseFloat(response['field_lesson_price']) || 0;
        const count = parseFloat(response['field_lesson_count']) || 0;
        const spsCode = String(response['field_sps_code'] ?? '');
        const subtotal = (spsCode === '554' || spsCode === '558') ? price : price * count;
        const discountValue = parseFloat(response['field_discount']) || 0;
        const discountType = response['field_discount_type'] === 'percentage' ? 'percentage' : 'flat';
        const discount = Math.round((discountType === 'percentage' ? subtotal * (discountValue / 100) : discountValue) * 100) / 100;
        this.selectedPackagePricing = { subtotal, discount, discountValue, discountType };

        this.giftCertificateForm.patchValue({
          giftCertificateAmount: Math.round((subtotal - discount) * 100) / 100,
          field_enrollment_lesson_count: response['field_lesson_count'],
          field_enrollment_lesson_price: response['field_lesson_price'],
        });

        this.calcNeccesaryFields();
      })

  }

  private mapSPSCodeToCategory(spsCode: string): string {
    const mapping = {
      '1394': '1395',  // DE -> DE
      '550': '51',     // EXT -> Extension
      '554': '5',      // MISC -> Miscellaneous
      '657': '66',     // Non Unit -> Non-Unit
      '549': '50',     // ORI -> Original
      '547': '49',     // PORI -> Pre-Original
      '552': '65',     // REN -> Renewals
      '558': '67'      // SUNDRY -> Sundry
    };

    return mapping[spsCode] || '70'; // Default to Uncategorized if no match
  }

  calcNeccesaryFields() {
    LoggerService.log('calcNeccesaryFields');
  }

  regexParamID(field) {
    if (!field) { return; }
    if (!isNaN(+field)) { return; }

    let regExp = /\(([^)]+)\)/g;
    let matches = field.match(regExp);

    return matches[matches.length - 1].replace(/[\(\)]/g, '');
  }
}
